import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { supabase } from '../lib/supabase';
import api from '../api';
import useCalendarStore from '../store/useCalendarStore';

export default function ForgotPasswordPage() {
  const darkMode = useCalendarStore(s => s.darkMode);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleResetRequest = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Email is required');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/api/auth/forgot-password', { email });
      setSubmitted(true);
      toast.success(res.data.message || 'Request submitted!');
    } catch (err) {
      toast.error(err.message || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center login-bg">
      <div className="w-full max-w-md px-4">
        {/* Header */}
        <div className="flex flex-col items-center justify-center mb-12">
          <div className="flex items-center gap-5 mb-4">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-blue-600/20 to-cyan-400/20 blur-2xl rounded-full opacity-60" />
              <img src={darkMode ? "/logo.png" : "/logo_black.png"} alt="ScholarsAnchor Logo" className="w-20 h-20 object-contain relative z-10" />
            </div>
            <div className="flex flex-col relative z-10 leading-[0.8]">
              <h1 className="text-5xl font-black text-zinc-900 dark:text-white tracking-tighter uppercase">
                Scholars
              </h1>
              <span className="text-xl font-black tracking-[0.32em] text-blue-600 dark:text-blue-500 uppercase">
                Anchor
              </span>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <div className="h-px w-16 bg-gradient-to-r from-transparent via-zinc-300 dark:via-zinc-700 to-transparent mb-4" />
            <p className="text-[0.7rem] sm:text-xs text-zinc-500 dark:text-zinc-400 uppercase tracking-[0.4em] font-black text-center max-w-[280px] sm:max-w-none leading-relaxed">
              Intelligent Academic Portal
            </p>
          </div>
        </div>
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-zinc-200 dark:border-zinc-800 p-8">
          <div className="flex items-center gap-2 mb-6">
            <Mail className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-zinc-800 dark:text-zinc-100">Forgot Password</h2>
          </div>

          {!submitted ? (
            <>
              <p className="text-xs text-zinc-500 mb-6">
                Enter your email address to submit a password reset request to your team's Super Admin.
              </p>
              <form onSubmit={handleResetRequest} className="space-y-4">
                <div>
                  <Label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Email Address</Label>
                  <Input
                    className="mt-1.5"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11 text-sm font-medium"
                  disabled={loading}
                >
                  {loading ? 'Sending link...' : 'Send Reset Link'}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 p-4 rounded-lg mb-6 text-sm">
                Your password reset request has been submitted for review.
                Please contact your team administrator for approval.
              </div>
              <p className="text-xs text-zinc-500">
                Didn't receive the email? Check your spam folder or try again in a few minutes.
              </p>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-zinc-100 dark:border-zinc-800">
            <Link to="/login" className="flex items-center justify-center gap-2 text-xs text-zinc-500 hover:text-blue-600 transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
